import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@core/components/ui/Logo';
import { useAppSelector } from '@core/store/hooks';
import { selectCurrentUser } from '@features/auth';
import { SidebarNavigation } from './SidebarNavigation';
import { SidebarUserDropdown } from './SidebarUserDropdown';
import { useScrollDirection } from './hooks';

/**
 * Main Sidebar component
 * Provides navigation, user menu, and responsive toggle functionality
 * @param {Object} props
 * @param {Function} props.onLogout - Logout handler function
 */
export function Sidebar({ onLogout }) {
  const { t } = useTranslation();
  const showHeader = useScrollDirection(50);
  const user = useAppSelector(selectCurrentUser);
  const logoUrl = user?.account?.logo_url;

  return (
    <>
      {/* Mobile Toggle Header */}
      <div className={`fixed top-0 inset-x-0 z-20 bg-sidebar border-b border-zinc-100 px-4 sm:px-6 lg:px-8 lg:hidden transition-transform duration-300 ${showHeader ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="flex items-center py-2">
          <button
            type="button"
            className="size-8 flex justify-center items-center gap-x-2 border border-zinc-200 text-muted hover:text-brand rounded-lg focus:outline-none focus:text-brand disabled:opacity-50 disabled:pointer-events-none"
            aria-haspopup="dialog"
            aria-expanded="false"
            aria-controls="hs-application-sidebar"
            aria-label="Toggle navigation"
            data-hs-overlay="#hs-application-sidebar"
          >
            <span className="sr-only">Toggle Navigation</span>
            <Icon icon="lucide:panel-left-open" className="shrink-0" width="20" height="20" />
          </button>
          <ol className="ms-3 flex items-center whitespace-nowrap">
            <li className="flex items-center">
              <Logo className="h-6 w-auto text-brand" />
            </li>
          </ol>
        </div>
      </div>

      {/* Sidebar Container */}
      <div
        id="hs-application-sidebar"
        className="hs-overlay [--auto-close:lg] [--is-layout-affect:true] hs-overlay-minified:w-[60px] lg:block lg:translate-x-0 lg:end-auto lg:bottom-0 w-72 hs-overlay-open:translate-x-0 -translate-x-full transition-all duration-300 transform h-full hidden overflow-visible fixed top-0 start-0 bottom-0 z-[60] bg-sidebar border-e border-zinc-200"
        role="dialog"
        tabIndex="-1"
        aria-label="Sidebar"
      >
        <div className="relative flex flex-col h-full max-h-full">
          {/* Header with Logo and Toggle (Expanded) */}
          <div className="py-4 px-2 flex justify-between items-center gap-x-2 hs-overlay-minified:hidden">
            <Link
              to="/"
              className="flex-none inline-block focus:outline-none focus:opacity-80 pl-2"
            >
              <Logo className={`${logoUrl ? 'max-h-14' : 'h-7'} w-auto text-brand`} src={logoUrl} />
            </Link>

            {/* Toggle Button - Expanded */}
            <div className="hs-tooltip [--placement:right] [--trigger:hover]">
              <button
                type="button"
                className="cursor-pointer lg:flex hidden justify-center items-center flex-none gap-x-3 size-9 text-sm text-muted hover:bg-brand/5 rounded-full focus:outline-none focus:bg-brand/5"
                data-hs-overlay-minifier="#hs-application-sidebar"
                aria-label="Minify navigation"
              >
                <Icon
                  icon="lucide:panel-left-close"
                  className="shrink-0"
                  width="20"
                  height="20"
                />
              </button>
              <span
                className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-[100] py-2 px-3 bg-white text-muted text-sm font-medium rounded-lg shadow-lg border border-zinc-200 whitespace-nowrap"
                role="tooltip"
              >
                {t('sidebar.collapse')}
              </span>
            </div>
          </div>

          {/* Navigation Content */}
          <SidebarNavigation />

          {/* Footer with User Dropdown and Toggle (Collapsed) */}
          <div className="mt-auto p-3 hs-overlay-minified:flex hs-overlay-minified:flex-col hs-overlay-minified:items-center">
            <SidebarUserDropdown onLogout={onLogout} />

            {/* Toggle Button - Collapsed */}
            <div className="hidden hs-overlay-minified:block hs-tooltip [--placement:right] [--trigger:hover] mt-3">
              <button
                type="button"
                className="cursor-pointer flex justify-center items-center flex-none gap-x-3 size-9 text-sm text-muted hover:bg-brand/5 rounded-full focus:outline-none focus:bg-brand/5"
                data-hs-overlay-minifier="#hs-application-sidebar"
                aria-label="Expand navigation"
              >
                <Icon
                  icon="lucide:panel-left-open"
                  className="shrink-0"
                  width="20"
                  height="20"
                />
              </button>
              <span
                className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-[100] py-2 px-3 bg-white text-muted text-sm font-medium rounded-lg shadow-lg border border-zinc-200 whitespace-nowrap"
                role="tooltip"
              >
                {t('sidebar.expand')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
